/* eslint-disable no-unused-vars */
import {createSlice} from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import get from 'lodash.get';
import httpClient from '../utils/httpClient';
import getTranslations from '../utils/getTranslations';
import {
    getLocaleCookieExpiration,
    getThemeCookieExpiration,
    getGeneralCookieExpiration
} from '@src/server/utils/getCookieExpirationDate';
import {getLoginNews, getLoginNotifications} from '../../api/resource/api';
import {
    BASE_PATH,
    DASHBOARD_PATH,
    defaultIntroductionInfo,
    DEFAULT_LOCALE,
    THEMES
} from '../../constants';

export const appSlice = createSlice({
    name: 'app',
    initialState: {
        translations: {},
        locale: 'nl',
        theme: THEMES.LIGHT,
        config: {},
        refererUrl: DASHBOARD_PATH,
        isNavOpen: false,
        shouldShowGDPRBunner: true,
        news: [],
        notifications: [],
        infoLinks: [],
        lastLoadedNewsLocale: '',
        lastLoadedNotificationsLocale: '',
        isFetchingNews: false,
        isFetchingNotifications: false,
        introductionInfo: defaultIntroductionInfo,
        isGlobalLoading: false,
        survicateWorkspaceKey: '',
        authRedirectUrl: null,
        isDirectAuth: false,
        isLast30DaysFilter: false
    },
    reducers: {
        setLocale: (state, action) => {
            const {
                locale,
                shouldSetCookie
            } = action.payload;
            state.locale = locale;
            if (shouldSetCookie) {
                Cookies.set('locale', locale, {sameSite: 'none', secure: true, expires: getLocaleCookieExpiration()});
            }
        },
        setTheme: (state, action) => {
            const {
                theme = THEMES.LIGHT,
                shouldSetCookie
            } = action.payload;
            state.theme = theme;
            if (shouldSetCookie) {
                Cookies.set('theme', theme, {sameSite: 'none', secure: true, expires: getThemeCookieExpiration()});
            }
        },
        setIsLast30DaysFilter: (state, action) => {
            const {isLast30DaysFilter, shouldSetCookie} = action.payload;
            state.isLast30DaysFilter = isLast30DaysFilter;
            if (shouldSetCookie) {
                Cookies.set('last30Days', String(isLast30DaysFilter), {sameSite: 'none', secure: true, expires: getGeneralCookieExpiration()});
            }
        },
        setTranslations: (state, action) => {
            state.translations = action.payload;
        },
        resetState: () => ({
            translations: {},
            locale: 'nl',
            theme: THEMES.LIGHT,
            config: {},
            refererUrl: DASHBOARD_PATH,
            isNavOpen: false,
            shouldShowGDPRBunner: true,
            news: [],
            notifications: [],
            infoLinks: [],
            lastLoadedNewsLocale: '',
            lastLoadedNotificationsLocale: '',
            isFetchingNews: false,
            isFetchingNotifications: false,
            introductionInfo: defaultIntroductionInfo,
            isGlobalLoading: false,
            inMomentAccountToken: '',
            authRedirectUrl: null,
            isDirectAuth: false,
            isLast30DaysFilter: false
        }),
        setConfig: (state, action) => {
            state.config = action.payload;
        },
        setRefererUrl: (state, action) => {
            state.refererUrl = action.payload;
        },
        setIsNavOpen: (state, action) => {
            state.isNavOpen = action.payload;
        },
        setShouldShowGDPRBunner: (state, action) => {
            state.shouldShowGDPRBunner = action.payload;
        },
        setNews: (state, action) => {
            state.news = action.payload;
        },
        setNotifications: (state, action) => {
            state.notifications = action.payload;
        },
        setInfoLinks: (state, action) => {
            state.infoLinks = action.payload;
        },
        setLastLoadedNewsLocale: (state, action) => {
            state.lastLoadedNewsLocale = action.payload;
        },
        setLastLoadedNotificationsLocale: (state, action) => {
            state.lastLoadedNotificationsLocale = action.payload;
        },
        setIsFetchingNews: (state, action) => {
            state.isFetchingNews = action.payload;
        },
        setIsFetchingNotifications: (state, action) => {
            state.isFetchingNotifications = action.payload;
        },
        setIntroductionInfo: (state, action) => {
            if (action.payload.reset) {
                state.introductionInfo = defaultIntroductionInfo;
            } else {
                state.introductionInfo = {
                    ...state.introductionInfo,
                    ...action.payload,
                    validation: {
                        ...state.introductionInfo.validation,
                        ...action.payload.validation
                    }
                };
            }
        },
        setIsGlobalLoading: (state, action) => {
            state.isGlobalLoading = action.payload;
        },
        setSurvicateWorkspaceKey: (state, action) => {
            state.survicateWorkspaceKey = action.payload;
        },
        setAuthRedirectUrl: (state, action) => {
            state.authRedirectUrl = action.payload;
        },
        setIsDirectAuth: (state, action) => {
            state.isDirectAuth = action.payload;
        }
    }
});

export const {
    setLocale,
    setTheme,
    setTranslations,
    resetState,
    setConfig,
    setRefererUrl,
    setIsNavOpen,
    setShouldShowGDPRBunner,
    setNews,
    setNotifications,
    setInfoLinks,
    setLastLoadedNewsLocale,
    setLastLoadedNotificationsLocale,
    setIsFetchingNews,
    setIsFetchingNotifications,
    setIntroductionInfo,
    setIsGlobalLoading,
    setSurvicateWorkspaceKey,
    setAuthRedirectUrl,
    setIsDirectAuth,
    setIsLast30DaysFilter
} = appSlice.actions;
export const loadTranslations = (localeParam) => async (dispatch, getState) => {
    const {locale} = getState().app;

    try {
        const translations = await getTranslations(localeParam || locale);
        dispatch(setTranslations(translations));
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Could not load messages for '${localeParam || locale}', keeping the current ones`, error);
    }
};

export const fetchNews = ({
    locale: fallbackLocale,
    options = {},
    isFallback = false
} = {}) => async (dispatch, getState) => {
    const {locale} = getState().app;
    const localeToFetch = fallbackLocale || locale;
    dispatch(setIsFetchingNews(true));

    try {
        const responseBody = await getLoginNews(localeToFetch, options);
        const news = get(responseBody, 'data.content.items', []);

        dispatch(setNews(news));
        if (!isFallback) {
            dispatch(setLastLoadedNewsLocale(localeToFetch));
        }
        dispatch(setIsFetchingNews(false));
        return responseBody;
    } catch (err) {
        dispatch(setIsFetchingNews(false));
        if (!isFallback) {
            dispatch(setLastLoadedNewsLocale(localeToFetch));
        }
        if (localeToFetch !== DEFAULT_LOCALE) {
            await dispatch(fetchNews({locale: DEFAULT_LOCALE, isFallback: true}));
        }
        return false;
    }
};

export const fetchNotifications = ({
    locale: fallbackLocale,
    options = {},
    isFallback = false
} = {}) => async (dispatch, getState) => {
    const {locale} = getState().app;
    const localeToFetch = fallbackLocale || locale;
    dispatch(setIsFetchingNotifications(true));

    try {
        const responseBody = await getLoginNotifications(localeToFetch, options);
        const notifications = get(responseBody, 'data.content.notifications', []);

        dispatch(setNotifications(notifications));
        if (!isFallback) {
            dispatch(setLastLoadedNotificationsLocale(localeToFetch));
        }
        dispatch(setIsFetchingNotifications(false));
        return responseBody;
    } catch (err) {
        dispatch(setIsFetchingNotifications(false));
        if (!isFallback) {
            dispatch(setLastLoadedNotificationsLocale(localeToFetch));
        }
        if (localeToFetch !== DEFAULT_LOCALE) {
            await dispatch(fetchNotifications({locale: DEFAULT_LOCALE, isFallback: true}));
        }
        return false;
    }
};

export const fetchInfoLinks = ({
    baseUrl,
    headers,
    locale: fallbackLocale,
    options = {}
} = {}) => async (dispatch, getState) => {
    const requestOptions = {};
    const {locale} = getState().app;
    const localeToFetch = fallbackLocale || locale;
    const servicePath = `api/custom/${localeToFetch}/block-information`;
    const url = baseUrl ? `${baseUrl}/${servicePath}` : `${BASE_PATH}/public/${servicePath}`;

    if (headers) {
        requestOptions.headers = headers;
    }

    if (options.timeout) {
        requestOptions.timeout = options.timeout;
    }

    try {
        const result = await httpClient.get(url, requestOptions);
        const links = get(result, 'data.items', []);
        dispatch(setInfoLinks(links));
        return result.data;
    } catch (err) {
        if (localeToFetch !== DEFAULT_LOCALE) {
            dispatch(fetchInfoLinks({locale: DEFAULT_LOCALE, options}));
        }
        return false;
    }
};

export const updateIntroductionInfo = (data) => (dispatch) => {
    dispatch(setIntroductionInfo(data));
};

export const selectTranslations = (state) => state.app.translations;
export const selectLocale = (state) => state.app.locale;
export const selectTheme = (state) => state.app.theme;
export const selectConfig = (state) => state.app.config;
export const selectRefererUrl = (state) => state.app.refererUrl;
export const selectIsNavOpen = (state) => state.app.isNavOpen;
export const selectShouldShowGDPRBunner = (state) => state.app.shouldShowGDPRBunner;
export const selectNews = (state) => state.app.news;
export const selectNotifications = (state) => state.app.notifications;
export const selectInfoLinks = (state) => state.app.infoLinks;
export const selectIsFetchingNews = (state) => state.app.isFetchingNews;
export const selectIsFetchingNotifications = (state) => state.app.isFetchingNotifications;
export const selectLastLoadedNewsLocale = (state) => state.app.lastLoadedNewsLocale;
export const selectLastLoadedNotificationsLocale = (state) => state
    .app.lastLoadedNotificationsLocale;
export const selectIntroductionInfo = (state) => state.app.introductionInfo;
export const selectIsGlobalLoading = (state) => state.app.isGlobalLoading;
export const selectSurvicateWorkspaceKey = (state) => state.app.survicateWorkspaceKey;
export const selectAuthRedirectUrl = (state) => state.app.authRedirectUrl;
export const selectIsDirectAuth = (state) => state.app.isDirectAuth;
export const selectIsLast30DaysFilter = (state) => state.app.isLast30DaysFilter;

export default appSlice.reducer;
